import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { WeeklySchedule } from 'src/screens/OnboardingTrainer/screens/Schedule/utils/timezoneUtils';
import { ScheduleConflict } from './useCheckScheduleConflicts';

export interface UpdateSchedulePayload {
  uid: string;
  timezone: string;
  weekly_schedule: WeeklySchedule;
  days?: number;
  force?: boolean;
}

export interface UpdateScheduleResponse {
  status: string;
  hasConflicts?: boolean;
  conflicts?: ScheduleConflict[];
  message?: string;
}

export function useUpdateTrainerSchedule() {
  const queryClient = useQueryClient();

  return useMutation<UpdateScheduleResponse, Error, UpdateSchedulePayload>({
    mutationFn: async (payload: UpdateSchedulePayload) => {
      const { uid, timezone, weekly_schedule, days = 60, force = false } = payload;

      if (!uid) {
        throw new Error('User UID is required to update schedule');
      }

      const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_UPDATE_SCHEDULE}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid, timezone, weekly_schedule, days, force }),
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to update schedule');
      }

      return response.json();
    },
    onSuccess: (res, variables) => {
      if (res?.hasConflicts) {
        return; // Don't hydrate cache if there were unresolved conflicts
      }

      const scheduleQueryKey = ['trainer-schedule', variables.uid];
      const onboardingQueryKey = ['onboarding-state', variables.uid];

      // Update schedule query cache immediately
      queryClient.setQueryData(scheduleQueryKey, {
        status: 'ok',
        data: {
          timezone: variables.timezone,
          weekly_schedule: variables.weekly_schedule,
        },
      });

      // Update onboarding query cache
      queryClient.setQueryData(onboardingQueryKey, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          onboarding: {
            ...oldData.onboarding,
            step4_schedule: {
              timezone: variables.timezone,
              weekly_schedule: variables.weekly_schedule,
            },
          },
          profile: {
            ...oldData.profile,
            timezone: variables.timezone,
            weekly_schedule: variables.weekly_schedule,
          },
        };
      });

      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
      queryClient.invalidateQueries({ queryKey: onboardingQueryKey });
    },
  });
}
